// Privacy policy at /privacy.
//
// Replaced July 23 2026 with the canonical policy (AI data-sharing
// consent lane). The canonical copy lives on the marketing site at
// haiven.co/privacy; THIS COMPONENT MUST STAY IN SYNC WITH IT — any
// policy change updates both in the same PR (standing rule). It renders
// the full text in-app rather than redirecting because an external
// redirect inside the Capacitor WebView navigates away with no way back.
//
// Apple-required HealthKit language (never for advertising / marketing /
// data-mining, never disclosed for those purposes) lives in the Health
// Data callout — edit those sentences only with a lawyer in the room.
// The error-monitoring claims are enforced by code: Sentry runs with
// send_default_pii=False, include_local_variables=False, and a
// before_send scrub (app/main.py), and payload contents are pinned by
// tests/test_ai_payload_privacy.py.

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .priv-root {
    background: #F2EDE3;
    color: #1C1814;
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    line-height: 1.8;
    min-height: 100vh;
  }

  .priv-header {
    padding: max(40px, calc(env(safe-area-inset-top) + 12px)) 48px 32px;
    border-bottom: 0.5px solid rgba(158, 132, 86, 0.2);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .priv-wordmark {
    font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
    font-size: 22px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: #1C1814;
    text-decoration: none;
  }

  .priv-back {
    font-size: 12px;
    color: #9E8456;
    text-decoration: none;
    letter-spacing: 0.06em;
  }

  .priv-hero {
    padding: 80px 48px 48px;
    max-width: 760px;
    margin: 0 auto;
  }

  .priv-eyebrow {
    font-size: 10px;
    letter-spacing: 0.14em;
    color: #9E8456;
    font-weight: 500;
    margin-bottom: 16px;
    text-transform: uppercase;
  }

  .priv-h1 {
    font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
    font-size: clamp(36px, 5vw, 52px);
    font-weight: 300;
    line-height: 1.15;
    color: #1C1814;
    margin-bottom: 20px;
  }

  .priv-meta {
    font-size: 13px;
    color: #6B6359;
    padding-bottom: 48px;
    border-bottom: 0.5px solid rgba(158, 132, 86, 0.2);
  }

  .priv-content {
    max-width: 760px;
    margin: 0 auto;
    padding: 0 48px 100px;
  }

  .priv-section {
    padding: 48px 0;
    border-bottom: 0.5px solid rgba(158, 132, 86, 0.2);
  }
  .priv-section:last-child { border-bottom: none; }

  .priv-section h2 {
    font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
    font-size: 24px;
    font-weight: 400;
    color: #1C1814;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .priv-section h2::before {
    content: '';
    display: block;
    width: 24px;
    height: 1px;
    background: #9E8456;
    flex-shrink: 0;
  }

  .priv-section h3 {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.06em;
    color: #1C1814;
    margin: 28px 0 10px;
    text-transform: uppercase;
  }

  .priv-section p {
    font-size: 15px;
    color: #6B6359;
    margin-bottom: 16px;
    line-height: 1.85;
  }

  .priv-section ul {
    margin: 8px 0 16px 0;
    padding-left: 0;
    list-style: none;
  }

  .priv-section ul li {
    font-size: 15px;
    color: #6B6359;
    padding: 6px 0 6px 20px;
    position: relative;
    line-height: 1.7;
  }
  .priv-section ul li::before {
    content: '\\2022';
    position: absolute;
    left: 0;
    color: #9E8456;
  }

  .priv-strong {
    color: #1C1814;
    font-weight: 500;
  }

  .priv-highlight-box {
    background: white;
    border: 0.5px solid rgba(158, 132, 86, 0.2);
    border-radius: 12px;
    padding: 24px 28px;
    margin: 24px 0;
  }
  .priv-highlight-box p {
    margin-bottom: 0;
    font-size: 14px;
  }

  .priv-health-callout {
    background: #1C1814;
    border-radius: 12px;
    padding: 28px 32px;
    margin: 24px 0;
  }
  .priv-health-callout .priv-eyebrow {
    margin-bottom: 12px;
  }
  .priv-health-callout p {
    color: rgba(242, 237, 227, 0.7);
    font-size: 14px;
    margin-bottom: 0;
  }

  .priv-contact-card {
    background: white;
    border: 0.5px solid rgba(158, 132, 86, 0.2);
    border-radius: 14px;
    padding: 32px;
    margin-top: 32px;
  }
  .priv-contact-card .priv-eyebrow { margin-bottom: 8px; }
  .priv-contact-card a {
    color: #9E8456;
    text-decoration: none;
    font-size: 15px;
  }
  .priv-contact-card p { margin-bottom: 4px; }

  .priv-link { color: #9E8456; text-decoration: none; }

  @media (max-width: 600px) {
    .priv-header { padding: 24px 24px 20px; }
    .priv-hero { padding: 48px 24px 32px; }
    .priv-content { padding: 0 24px 80px; }
  }
`;


const Strong = ({ children }) => <span className="priv-strong">{children}</span>;


export default function PrivacyPage() {
  return (
    <div className="priv-root">
      <style>{STYLES}</style>

      <header className="priv-header">
        <a href="https://haiven.co" className="priv-wordmark">HAIVEN</a>
        <a href="/" className="priv-back">← Back to app</a>
      </header>

      <div className="priv-hero">
        <p className="priv-eyebrow">Legal</p>
        <h1 className="priv-h1">Privacy Policy</h1>
        <p className="priv-meta">
          Haiven Labs LLC &nbsp;·&nbsp; Effective: September 13, 2026
          &nbsp;·&nbsp; Last updated: September 13, 2026
        </p>
      </div>

      <div className="priv-content">

        <section className="priv-section">
          <h2>Summary</h2>
          <p>
            Haiven is operated by <Strong>Haiven Labs LLC</Strong> ("Haiven,"
            "we," "us"). This policy explains what information we collect, how
            we collect it, how we use it, who we share it with, and the choices
            you have.
          </p>
          <ul>
            <li>
              Haiven collects what you write, what you self-report, and health
              metrics from wearables you choose to connect.
            </li>
            <li>
              We use an external AI provider — <Strong>Anthropic (Claude)</Strong> —
              to generate written insights, summaries, and report narratives.{' '}
              <Strong>
                This requires your explicit, separate consent, and you can
                decline or withdraw it at any time.
              </Strong>
            </li>
            <li>
              We never send raw wearable readings (such as heart rate
              variability, resting heart rate, or sleep measurements) to any AI
              provider. Only category labels derived by our own scoring engine
              are sent.
            </li>
            <li>We do not sell your personal information.</li>
            <li>
              You can withdraw consent, export, or delete your data at any
              time.
            </li>
          </ul>
          <p>
            If you have questions about anything here, contact us at{' '}
            <a href="mailto:hello@haiven.co" className="priv-link">
              hello@haiven.co
            </a>.
          </p>
        </section>

        <section className="priv-section">
          <h2>Information We Collect and How We Collect It</h2>

          <h3>Information you provide directly</h3>
          <ul>
            <li>
              <Strong>Account details</Strong> — email address, password
              credentials, and (optionally) your name, entered when you create
              an account
            </li>
            <li>
              <Strong>Free-text you write</Strong> — daily check-in entries,
              journal entries, and chat messages you type into the app
            </li>
            <li>
              <Strong>Onboarding and values-profile answers</Strong>, including
              free-text responses, entered during onboarding and the
              Foundations questionnaire
            </li>
            <li>
              <Strong>Self-reported ratings and sliders</Strong> used in daily
              check-ins
            </li>
            <li>
              <Strong>Date of birth and demographic details</Strong>, entered
              during onboarding
            </li>
            <li>
              <Strong>Sharing reminder preferences</Strong>, for example whether
              you want a reminder to share your summary and on which day,
              entered in your profile settings
            </li>
            <li>
              <Strong>Payment information</Strong> — collected and processed
              directly by Stripe; Haiven does not receive or store full payment
              card numbers
            </li>
          </ul>

          <h3>Information from wearables and health services you connect</h3>
          <p>
            If — and only if — you choose to connect a device or service, we
            receive health metrics from it. This is optional; Haiven works
            without any wearable connected.
          </p>
          <ul>
            <li>
              <Strong>Oura</Strong> (web app only) — connected via OAuth authorization. We
              receive sleep, readiness, activity, and heart-rate data from
              Oura's service. Haiven does not pair with, connect to, or control
              the Oura ring itself; your ring syncs to Oura, and Haiven reads
              from Oura's service with your authorization.
            </li>
            <li>
              <Strong>Apple Health (HealthKit)</Strong> — with your permission,
              we read heart rate variability, sleep, resting heart rate, and
              step data from your device.
            </li>
          </ul>
          <p>
            You can disconnect any of these at any time in the app, and revoke
            access from the provider's own settings (or, for Apple Health, in
            iOS Settings → Privacy &amp; Security → Health).
          </p>
          <div className="priv-health-callout">
            <p className="priv-eyebrow">Health Data</p>
            <p>
              Haiven does not use Apple HealthKit data for advertising,
              marketing, or data-mining, and does not disclose HealthKit data
              to third parties for those purposes.
            </p>
          </div>

          <h3>Information collected automatically</h3>
          <ul>
            <li>
              <Strong>Error and crash diagnostics</Strong> — collected by
              Sentry, automatically when errors occur, to diagnose and fix
              problems
            </li>
            <li>
              <Strong>Basic usage and app events</Strong> — logged by our
              backend as you use the app, to operate and improve the service
            </li>
          </ul>
          <p>
            Crash reports contain technical error data — the type of error,
            where in the app it occurred, and device or browser information —
            together with an internal account identifier.{' '}
            <Strong>They never include journal text, messages, or health data:</Strong>{' '}
            our error monitoring is configured to exclude request contents and
            in-memory variables from every report. Sentry is the only
            error-monitoring tool we use, and we use no advertising or
            product-analytics services.
          </p>
        </section>

        <section className="priv-section">
          <h2>How We Use Your Information</h2>
          <p>We use the information described above to:</p>
          <ul>
            <li>Create and maintain your account and authenticate you</li>
            <li>Compute your Signal scores, domain scores, trends, and history</li>
            <li>
              Generate written insights, summaries, and report narratives (see
              "Use of a Third-Party AI Service" below)
            </li>
            <li>
              Produce summaries you can choose to share with anyone you
              choose
            </li>
            <li>
              Support practitioner features, if you are linked to a
              practitioner and have consented to that link
            </li>
            <li>Process payments and manage subscriptions</li>
            <li>Diagnose errors, maintain security, and improve the service</li>
            <li>Communicate with you about the service</li>
            <li>Comply with legal obligations</li>
          </ul>
          <div className="priv-highlight-box">
            <p>
              <Strong>
                We do not sell your personal information, and we do not use it
                for advertising or behavioral targeting.
              </Strong>
            </p>
          </div>
        </section>

        <section className="priv-section">
          <h2>Use of a Third-Party AI Service (Anthropic)</h2>
          <p>
            <Strong>
              This section describes data shared with a third-party AI
              provider, and is subject to your separate, explicit consent.
            </Strong>
          </p>
          <p>
            Haiven uses <Strong>Anthropic's Claude models, accessed through
            Anthropic's API</Strong>, to generate written content — insights,
            narrative summaries, report language, chat responses, and to help
            categorize the themes present in what you write.
          </p>

          <h3>What is sent to Anthropic</h3>
          <p>
            When you have granted AI data-sharing consent, the following may be
            transmitted to Anthropic:
          </p>
          <ul>
            <li>
              <Strong>Text you have written</Strong> — daily check-in entries,
              journal entries, chat messages, and free-text answers from
              onboarding and the values profile.
            </li>
            <li>
              <Strong>Numeric wellness scores and trends</Strong> computed by
              Haiven — your Signal score, domain scores, streaks, and changes
              over time.
            </li>
            <li>
              <Strong>Category labels derived from wearable data</Strong> — for
              example, sleep, recovery, or energy described as "high,"
              "medium," "low," or "unclear."
            </li>
          </ul>

          <h3>What is never sent to Anthropic</h3>
          <ul>
            <li>
              <Strong>Raw wearable readings are never sent.</Strong> Specific
              heart rate variability values, resting heart rate values, sleep
              scores, and readiness scores are not transmitted to Anthropic.
              These are processed by Haiven's own scoring engine, and only the
              resulting category label is included in any request.
            </li>
            <li>
              <Strong>Direct identifiers are not sent.</Strong> Requests to
              Anthropic do not include your name, email address, account
              identifier, date of birth, or payment information.
            </li>
            <li>
              <Strong>Your data is not used to train AI models.</Strong>{' '}
              Anthropic's Commercial Terms, which govern API use, state that
              Anthropic may not train models on Customer Content — which
              includes both what we send and what is returned.
            </li>
          </ul>

          <h3>Who receives it and for what purpose</h3>
          <p>
            The recipient is <Strong>Anthropic, PBC</Strong>, which processes
            the request and returns generated text. The purpose is limited to
            producing the insight, summary, narrative, or categorization you
            see in the app. Anthropic automatically deletes API inputs and
            outputs from its systems within 30 days, except where longer
            retention is required by law or to enforce its usage policies.
          </p>
          <p>
            Anthropic's privacy practices are described at{' '}
            <a
              href="https://www.anthropic.com/legal/privacy"
              className="priv-link"
            >
              anthropic.com/legal/privacy
            </a>.
          </p>

          <h3>Your consent, and what happens if you decline</h3>
          <p>
            We ask for your consent to AI data sharing{' '}
            <Strong>before any of your content is sent</Strong>, during
            onboarding. Consent is affirmative and separate — it is not bundled
            into our Terms of Service, and it is not pre-selected.
          </p>
          <p>
            <Strong>If you decline, Haiven continues to work.</Strong>{' '}
            Specifically:
          </p>
          <ul>
            <li>
              Your <Strong>total Signal score</Strong>,{' '}
              <Strong>Body domain score</Strong>, and{' '}
              <Strong>Signal history</Strong> are computed entirely by Haiven's
              own proprietary algorithms and remain fully available.
            </li>
            <li>
              Your <Strong>Mind and Behavior domain scores</Strong> remain
              available, computed from your self-reported check-in responses
              alone. They do not incorporate the additional layer normally
              derived from your written entries, so they reflect a narrower
              set of inputs.
            </li>
            <li>
              <Strong>Insights, summaries, and report text</Strong> are
              generated by Haiven's own rule-based system instead, using
              plainer language.
            </li>
            <li>
              <Strong>Deep Dive analysis is unavailable</Strong>, because it
              cannot be produced without the AI service. You will see an
              explanation where it would normally appear.
            </li>
          </ul>
          <p>
            You can change this decision at any time in your settings.{' '}
            <Strong>Withdrawing consent takes effect immediately</Strong> and
            stops further transmission of your content to Anthropic.
          </p>
        </section>

        <section className="priv-section">
          <h2>Sharing Your Information</h2>
          <p>We share information only as described here.</p>

          <h3>Service providers</h3>
          <p>
            We use the following providers to operate Haiven. Each is bound by
            contract to protect your information, to use it only to provide
            services to us, and to maintain protections at least equivalent to
            those described in this policy.
          </p>
          <ul>
            <li>
              <Strong>Anthropic</Strong> — AI text generation, as described
              above
            </li>
            <li>
              <Strong>Supabase</Strong> — database and authentication; account
              data, entries, scores
            </li>
            <li>
              <Strong>Render</Strong> — backend hosting; application processing
            </li>
            <li>
              <Strong>Vercel</Strong> — frontend hosting; application delivery
            </li>
            <li>
              <Strong>Stripe</Strong> — payments; payment and subscription data
            </li>
            <li>
              <Strong>Sentry</Strong> — error monitoring; technical error data
              and an internal user ID, never your written content or health
              data
            </li>
            <li>
              <Strong>Oura, Apple</Strong> — wearable data sources; health
              metrics you authorize
            </li>
          </ul>
          <p>
            Subscription payments are processed through our website, not inside
            the mobile app.
          </p>

          <h3>Subprocessors used by our providers</h3>
          <p>
            The providers listed above engage their own subprocessors to
            deliver their services. Each is contractually required to impose
            data protection obligations on those subprocessors that are
            substantially equivalent to the obligations it owes us, and to
            remain responsible for their compliance. We receive advance notice
            of changes to authorized subprocessors and can object to them.
          </p>

          <h3>Practitioners you choose to link with</h3>
          <p>
            If you link your account to a practitioner, and separately consent
            to that link, the practitioner can see the information disclosed to
            you at the time of consent — protocol completion and signal
            information.
          </p>
          <p>
            <Strong>
              Your journal and check-in text is never shared with a
              practitioner without your explicit consent and approval.
            </Strong>{' '}
            Where you choose to share written material, you control what is
            included — you may share selected excerpts, high-level summaries,
            or nothing at all, and Haiven works fully either way. You can
            revoke a practitioner link, or any individual share, at any time.
          </p>

          <h3>Reports you choose to share</h3>
          <p>
            When you generate a shareable report, you control whether written
            excerpts are included. Share links can be revoked by you at any
            time.
          </p>

          <h3>Legal and safety</h3>
          <p>
            We may disclose information if required by law, or where necessary
            to protect the rights, safety, or security of users, the public, or
            Haiven.
          </p>

          <h3>Business transfers</h3>
          <p>
            If Haiven is involved in a merger, acquisition, or sale of assets,
            information may be transferred as part of that transaction. We will
            notify you before your information becomes subject to a materially
            different privacy policy.
          </p>
        </section>

        <section className="priv-section">
          <h2>Research Use</h2>
          <p>
            Separately from AI processing, we may ask for your consent to use{' '}
            <Strong>de-identified</Strong> information for research and product
            improvement — for example, to study which behavioral protocols tend
            to produce better outcomes across groups of people, and to improve
            how Haiven interprets patterns.
          </p>
          <p>
            This is a distinct consent from AI data sharing, and it does not
            change how your Signal scores, history, or protocols are
            calculated or delivered to you.
          </p>
          <p>
            Research consent is also the gate on your written entries being
            shown to another person.{' '}
            <Strong>
              If you decline it, sharing journal entries with a linked
              practitioner is unavailable, and written excerpts cannot be
              included in reports you share.
            </Strong>{' '}
            That is separate from, and in addition to, the de-identified
            research use described above. Granting research consent does not disclose
            anything to anyone on its own — a practitioner sees written
            material only where you have also consented to the link and
            approved that specific entry, as described in "Sharing Your
            Information" above.
          </p>
          <ul>
            <li>
              De-identified means we remove the information that identifies you
              — your name, email address, and authentication credentials — and
              hold what remains under an internal reference that is no longer
              connected to your identity. This is individual-level information
              rather than a group average, so it is not anonymous in the
              strictest sense, and we do not attempt to re-identify it or
              combine it with other information to do so.
            </li>
            <li>
              We never sell or disclose raw individual data for research
              purposes.
            </li>
            <li>
              <Strong>
                We do not use your information — including anonymized or
                derived information — to train, fine-tune, or develop
                artificial intelligence models.
              </Strong>
            </li>
            <li>
              Where research findings inform features or published insights,
              those findings are reported in aggregate — no individual's data
              is identifiable within them.
            </li>
          </ul>
          <p>
            Because this information is de-identified, it may be retained after
            account deletion, as described in "Account Deletion and Data
            Retention" below.
            You can withdraw research consent at any time, which stops any
            further use of your information for these purposes and immediately
            ends any practitioner journal-entry sharing described above.
          </p>
        </section>

        <section className="priv-section">
          <h2>Account Deletion and Data Retention</h2>
          <p>
            We retain your information for as long as your account is active,
            so that your history, trends, and long-term patterns remain
            available to you — that continuity is a core part of what Haiven
            does.
          </p>
          <p>
            When you delete your Haiven account, we permanently delete your
            identity and login information, including your name, email
            address, and authentication credentials. You will no longer be able
            to access your account, and any data that remains is no longer
            linked to your identity in our systems.
          </p>
          <p>
            If you previously consented to research use of your data, a
            de-identified copy of your wellness check-in history,
            wearable-derived signals, and related app-generated data is
            retained after deletion, solely for research and
            product-improvement purposes. This retained data is held under an
            internal reference that is no longer connected to your name, email
            address, or any other information that identifies you, and we do
            not attempt to re-identify it or combine it with other information
            to do so. Free-text journal entries and chat messages you've
            written are never retained after deletion, regardless of your
            research consent status.
          </p>
          <p>
            If you did not consent to research use of your data, or haven't
            made a choice, all of your data is permanently deleted upon account
            deletion, with no retention of any kind beyond a minimal internal
            record — the date your account was created, the date of your last
            check-in, and the date of deletion — used solely for our own
            internal reporting. This record contains no content, no scores, and
            nothing that identifies you.
          </p>
          <p>
            You can review or change your research consent choice at any time
            in Settings, before you delete your account.
          </p>
          <p>
            To request deletion, contact{' '}
            <a href="mailto:hello@haiven.co" className="priv-link">
              hello@haiven.co
            </a>{' '}
            or use the in-app account deletion option.
          </p>
        </section>

        <section className="priv-section">
          <h2>Security</h2>
          <p>
            We protect your information using encryption in transit and at
            rest, access controls, row-level security on our database, and
            monitored infrastructure. No system is perfectly secure, but we
            work to protect your information and to respond promptly if a
            problem occurs.
          </p>
        </section>

        <section className="priv-section">
          <h2>Your Rights and Choices</h2>
          <p>Regardless of where you live, you can:</p>
          <ul>
            <li><Strong>Access</Strong> the information we hold about you</li>
            <li><Strong>Correct</Strong> inaccurate information</li>
            <li><Strong>Delete</Strong> your account and associated data</li>
            <li>
              <Strong>Withdraw consent</Strong> for AI data sharing or research
              use at any time
            </li>
            <li><Strong>Disconnect</Strong> any wearable or health service</li>
            <li>
              <Strong>Revoke</Strong> any share link or practitioner link
            </li>
          </ul>
          <p>
            To exercise any of these, use the in-app settings or contact{' '}
            <a href="mailto:hello@haiven.co" className="priv-link">
              hello@haiven.co
            </a>.
          </p>
          <p>
            Haiven is offered to users in the United States and is not offered
            in the European Union or the United Kingdom. Depending on your
            state of residence, you may have additional rights under laws such
            as the California Consumer Privacy Act, including the right to data
            portability and the right to know what information we have
            collected about you.
          </p>
        </section>

        <section className="priv-section">
          <h2>Children</h2>
          <p>
            Haiven is not intended for anyone under 18, and we do not knowingly
            collect information from anyone under 18. If we learn that we have,
            we will delete it. Our onboarding includes an age check.
          </p>
        </section>

        <section className="priv-section">
          <h2>Health Information</h2>
          <p>
            Haiven is a wellness product.{' '}
            <Strong>
              It does not diagnose, treat, cure, or prevent any disease or
              medical condition, and it is not a substitute for professional
              medical advice.
            </Strong>{' '}
            Information shown in the app is for your own reflection.
          </p>
          <p>
            Haiven is not a covered entity or business associate under HIPAA,
            and does not operate under a Business Associate Agreement. Haiven
            is a direct-to-consumer wellness service; the information you
            provide is not protected health information held by a healthcare
            provider. If this changes — for example, if Haiven begins working
            directly with licensed healthcare providers in a way that creates
            HIPAA obligations — we will update this policy and notify you
            before that change takes effect.
          </p>
        </section>

        <section className="priv-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this policy. When we do, we will revise the effective
            date shown at the top of this page, and we encourage you to review
            it periodically. Where the law requires your consent before a
            material change takes effect — for example, a change to how your
            information is shared with AI providers — we will ask for it.
          </p>
        </section>

        <section className="priv-section">
          <h2>Contact</h2>
          <div className="priv-contact-card">
            <p className="priv-eyebrow">Privacy Contact</p>
            <p><Strong>Haiven Labs LLC</Strong></p>
            <p>
              <a href="mailto:hello@haiven.co">hello@haiven.co</a>
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
